import { FC } from "react";
import { useTranslation } from "react-i18next";
import { DiscountItemType } from "src/Modules/Discounts/types";
import ImageWithEmptyFallback from "src/components/ImageWithEmptyFallback/ImageWithEmptyFallback";
import Badge from "src/components/Banner/Banner";
import { aggregatedData } from "../../../../constants";
import useRibbonLabelShift from "../../hooks/useRibbonLabelShift";

type DiscountCardImageProps = {
  imageUrl?: string;
  name?: string;
  items: DiscountItemType[];
  loading?: boolean;
};

const DiscountCardImage: FC<DiscountCardImageProps> = ({ imageUrl, name, items, loading }) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  const { ref, shift } = useRibbonLabelShift(isRtl);

  if (loading)
    return (
      <div className="bg-background-minimal flex aspect-square w-full items-center justify-center overflow-hidden rounded-md p-4 md:p-6">
        <div className="bg-background-subtle aspect-square h-full animate-pulse rounded-md" />
      </div>
    );

  return (
    <Badge
      degree={isRtl ? -45 : 45}
      distanceFromCenter={-100}
      className="bg-background-minimal flex! w-full overflow-hidden rounded-md"
      bannerClassname="bg-background-primary py-1! shadow-md"
      title={
        <span
          className="text-10 md:text-sm font-semibold tracking-wide whitespace-nowrap text-white flex justify-center text-center"
          style={{ transform: `translateX(${shift}px)` }}
        >
          {aggregatedData.discount.labelTransform(items, t)}
        </span>
      }
    >
      <div
        ref={ref}
        className="discount-card-image bg-background-minimal relative isolate flex w-full items-center justify-center overflow-hidden p-4 aspect-square md:p-6"
      >
        {imageUrl && (
          <img src={imageUrl} alt="" aria-hidden loading="lazy" className="absolute inset-0 -z-10 h-full w-full scale-150 object-cover blur-3xl opacity-50" />
        )}
        <ImageWithEmptyFallback
          src={imageUrl}
          alt={name}
          lazy
          className="aspect-square h-full! max-h-full w-auto! max-w-full rounded-md object-cover shadow-card transition-transform duration-300 group-hover:scale-105"
        />
      </div>
    </Badge>
  );
};

export default DiscountCardImage;
